import { Button, Typography } from "@cur8/maneki";
import { skipToken, useQuery } from "@tanstack/react-query";
import {
  isNoNekoLensAccess,
  useCreateNekoLensAuthUrl,
} from "hooks/queries/useCreateNekoLensAuthUrl";
import { CheckInParams } from "lib/types/checkInParameters";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useAppInsights } from "render/context/AppInsightsContext";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { Banner } from "render/ui/presentation/Banner";
import { SpinningLoader } from "render/ui/presentation/SpinningLoader";
import { Link } from "render/ui/trigger/Link";
import styles from "./styles.module.sass";

type NekoLensAuthViewProps = {
  onBack(): void;
} & CheckInParams;
// Pre-signed URL is valid for 5 minutes, this value needs to be less than that.
// Counted from when the URL arrived, or from the opening while there is none.
export const AUTO_HIDE_DELAY = 240000;

// The step flow mounts the view afresh for every opening, and each one gets a
// URL of its own.
let openings = 0;

function openNow() {
  return { id: ++openings, at: Date.now() };
}

export function NekoLensAuthView({
  onBack,
  patientId,
  visitId,
}: NekoLensAuthViewProps) {
  const appInsights = useAppInsights();
  const [opening] = useState(openNow);

  const authUrlQuery = useCreateNekoLensAuthUrl(opening.id);
  const url = authUrlQuery.data;
  const noAccess = isNoNekoLensAccess(authUrlQuery.error);

  const closesAt = (authUrlQuery.dataUpdatedAt || opening.at) + AUTO_HIDE_DELAY;

  // Timers don't run while the iPad sleeps, so one armed before it slept can
  // fire long after closesAt. Waking up arms it again against the clock.
  const [wakes, setWakes] = useState(0);

  useEffect(() => {
    function wake() {
      if (document.visibilityState === "visible") {
        setWakes((count) => count + 1);
      }
    }
    document.addEventListener("visibilitychange", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onBack();
    }, closesAt - Date.now());

    return () => {
      clearTimeout(timeout);
    };
  }, [closesAt, onBack, wakes]);

  const qrCodeQuery = useQuery({
    queryKey: ["nekoLensQrCode", patientId, visitId, url],
    queryFn:
      url == null ? skipToken : () => drawQrCode(patientId, visitId, url),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (qrCodeQuery.error != null) {
      appInsights.trackException({
        exception: qrCodeQuery.error,
        properties: { context: "Neko Lens QR code" },
      });
    }
  }, [appInsights, qrCodeQuery.error]);

  const failed =
    !authUrlQuery.isFetching && (authUrlQuery.isError || qrCodeQuery.isError);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.frame}>
              {qrCodeQuery.data != null ? (
                <img
                  src={qrCodeQuery.data}
                  className={styles.img}
                  alt="Neko Lens QR code"
                />
              ) : failed ? (
                <Banner
                  kind="error"
                  action={
                    noAccess ? undefined : (
                      <Link onPress={() => authUrlQuery.refetch()}>
                        Try again
                      </Link>
                    )
                  }
                >
                  {noAccess
                    ? "Your account doesn't have access to Neko Lens"
                    : "Could not create the QR code"}
                </Banner>
              ) : (
                <div
                  className={styles.loader}
                  role="status"
                  aria-label="Loading QR code"
                >
                  <SpinningLoader success={false} />
                </div>
              )}
            </div>

            <div className={styles.instructions}>
              <Typography variant="title-s" color="subtle">
                Scan the QR code with NekoLens to assign the member to the
                device
              </Typography>
            </div>
          </section>
        }
        buttons={
          <Button variant="secondary" fillContainer onPress={onBack}>
            Close
          </Button>
        }
      />
    </PageFrameContent>
  );
}

// Async so a URL constructQrBytes can't parse rejects like a failed draw does,
// rather than throwing.
async function drawQrCode(patientId: string, visitId: string, url: string) {
  return QRCode.toDataURL(
    [{ data: constructQrBytes(patientId, visitId, url), mode: "byte" }],
    { errorCorrectionLevel: "L" }
  );
}

export const constructQrBytes = (
  patientId: string,
  visitId: string,
  url: string
) => {
  const parts = url.split("=p1");
  const prefix = parts[0] + "=p1";
  const base64 = parts[1]!.slice(0, parts[1]!.length);

  const encoder = new TextEncoder();
  const idBytes = encoder.encode(patientId);
  const visitBytes = encoder.encode(visitId);
  const prefixBytes = encoder.encode(prefix);

  // For unpadded base64: calculate exact decoded length
  const fullBlocks = Math.floor(base64.length / 4);
  const remainder = base64.length % 4;
  const decodedLength = fullBlocks * 3 + (remainder > 0 ? remainder - 1 : 0);
  const bytes = new Uint8Array(
    idBytes.length + visitBytes.length + prefixBytes.length + decodedLength + 4
  );

  let i = 0;
  bytes[i++] = 0; // marker byte
  bytes.set(idBytes, i);
  i += idBytes.length;
  bytes[i++] = 0;
  bytes.set(visitBytes, i);
  i += visitBytes.length;
  bytes[i++] = 0;
  bytes.set(prefixBytes, i);
  i += prefixBytes.length;
  bytes[i++] = 0;
  urlSafeAtob(base64, bytes.subarray(i));

  return bytes;
};

const urlSafeAtob = (b64: string, bytes: Uint8Array) => {
  let i = 0;
  let j = 0;

  // Process complete 4-character blocks
  const fullBlocks = Math.floor(b64.length / 4);
  for (let block = 0; block < fullBlocks; block++, i += 4, j += 3) {
    const c0 = decode(b64.charCodeAt(i));
    const c1 = decode(b64.charCodeAt(i + 1));
    const c2 = decode(b64.charCodeAt(i + 2));
    const c3 = decode(b64.charCodeAt(i + 3));

    bytes[j] = (c0 << 2) | (c1 >> 4);
    bytes[j + 1] = ((c1 & 15) << 4) | (c2 >> 2);
    bytes[j + 2] = ((c2 & 3) << 6) | c3;
  }

  // Handle remaining 2 or 3 characters (unpadded base64)
  const remaining = b64.length % 4;
  if (remaining === 2) {
    const c0 = decode(b64.charCodeAt(i));
    const c1 = decode(b64.charCodeAt(i + 1));
    bytes[j] = (c0 << 2) | (c1 >> 4);
  } else if (remaining === 3) {
    const c0 = decode(b64.charCodeAt(i));
    const c1 = decode(b64.charCodeAt(i + 1));
    const c2 = decode(b64.charCodeAt(i + 2));
    bytes[j] = (c0 << 2) | (c1 >> 4);
    bytes[j + 1] = ((c1 & 15) << 4) | (c2 >> 2);
  }

  return bytes;
};

const decode = (b64: number) => {
  if (b64 >= 65 && b64 <= 90) {
    // A-Z
    return b64 - 65;
  }
  if (b64 >= 97 && b64 <= 122) {
    // a-z
    return b64 - 97 + 26;
  }
  if (b64 >= 48 && b64 <= 57) {
    // 0-9
    return b64 - 48 + 52;
  }
  if (b64 === 45) {
    // -
    return 62;
  }
  if (b64 === 95) {
    // _
    return 63;
  }
  return 0;
};
